import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LogIn, LogOut, Clock, Calendar, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeeClock() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [clockedIn, setClockedIn] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData) {
      navigate("/employee");
      return;
    }
    setUser(JSON.parse(userData));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      checkClockStatus();
      loadTimeEntries();
      const interval = setInterval(() => setCurrentTime(new Date()), 1000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const checkClockStatus = async () => {
    try {
      const response = await axios.get(`${API}/time-entries/status/${user.id}`);
      setClockedIn(response.data.clocked_in);
      setCurrentEntry(response.data.entry);
    } catch (error) {
      console.error("Error checking status:", error);
    }
  };

  const loadTimeEntries = async () => {
    try {
      const response = await axios.get(`${API}/time-entries/employee/${user.id}`);
      setTimeEntries(response.data);
    } catch (error) {
      console.error("Error loading entries:", error);
    }
  };

  const handleClockIn = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/time-entries/clock-in?employee_id=${user.id}`);
      toast.success("Erfolgreich eingestempelt!");
      await checkClockStatus();
      await loadTimeEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Einstempeln");
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/time-entries/clock-out?employee_id=${user.id}`);
      toast.success("Erfolgreich ausgestempelt!");
      await checkClockStatus();
      await loadTimeEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Ausstempeln");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/employee");
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="rounded-full"
            data-testid="logout-button"
          >
            Abmelden
          </Button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Willkommen, {user.name}!
          </h1>
          <div className="text-2xl text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }} data-testid="current-time">
            {format(currentTime, "HH:mm:ss", { locale: de })}
          </div>
          <div className="text-lg text-gray-500">
            {format(currentTime, "EEEE, dd. MMMM yyyy", { locale: de })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="shadow-xl rounded-3xl border-0">
            <CardContent className="p-8">
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className={`w-24 h-24 rounded-full flex items-center justify-center ${clockedIn ? 'bg-green-100' : 'bg-blue-100'}`}>
                    <LogIn className={`w-12 h-12 ${clockedIn ? 'text-green-600' : 'text-blue-600'}`} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Einstempeln</h3>
                  <p className="text-gray-600 text-sm">Arbeitsbeginn erfassen</p>
                </div>
                <Button
                  onClick={handleClockIn}
                  disabled={clockedIn || loading}
                  className="w-full h-16 text-lg rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors duration-200"
                  data-testid="clock-in-button"
                >
                  Einsteigen
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl rounded-3xl border-0">
            <CardContent className="p-8">
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className={`w-24 h-24 rounded-full flex items-center justify-center ${clockedIn ? 'bg-orange-100' : 'bg-gray-100'}`}>
                    <LogOut className={`w-12 h-12 ${clockedIn ? 'text-orange-600' : 'text-gray-400'}`} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Ausstempeln</h3>
                  <p className="text-gray-600 text-sm">Arbeitsende erfassen</p>
                </div>
                <Button
                  onClick={handleClockOut}
                  disabled={!clockedIn || loading}
                  className="w-full h-16 text-lg rounded-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 transition-colors duration-200"
                  data-testid="clock-out-button"
                >
                  Ausstempeln
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {clockedIn && currentEntry && (
          <Card className="shadow-xl rounded-3xl border-0 mb-8 bg-green-50" data-testid="current-session-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Clock className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-bold text-lg">Aktuell eingestempelt</p>
                    <p className="text-gray-600">
                      Seit {format(new Date(currentEntry.clock_in), "HH:mm", { locale: de })} Uhr
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">
                    {Math.floor((currentTime - new Date(currentEntry.clock_in)) / 1000 / 60 / 60)}h{' '}
                    {Math.floor(((currentTime - new Date(currentEntry.clock_in)) / 1000 / 60) % 60)}m
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="shadow-xl rounded-3xl border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              Meine Zeiten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {timeEntries.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Noch keine Einträge vorhanden</p>
              ) : (
                timeEntries.slice(0, 10).map((entry) => (
                  <div key={entry.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-2xl" data-testid={`time-entry-${entry.id}`}>
                    <div>
                      <p className="font-semibold">{format(new Date(entry.date), "dd.MM.yyyy", { locale: de })}</p>
                      <p className="text-sm text-gray-600">
                        {format(new Date(entry.clock_in), "HH:mm", { locale: de })} - 
                        {entry.clock_out ? format(new Date(entry.clock_out), "HH:mm", { locale: de }) : " Offen"}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{formatDuration(entry.duration_minutes)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
